import React, { useState, useEffect, useContext } from 'react';
import { 
    Box, Grid, Paper, Typography, TextField, Button, 
    IconButton, Stack, Divider, InputAdornment, 
    useTheme, useMediaQuery, Drawer, Fab, Badge,
    Chip, CardActionArea, Skeleton, Avatar
} from '@mui/material';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
    faSearch, faTrashAlt, faMoneyBillWave, 
    faPlus, faMinus, faUserXmark, faShoppingCart, faTimes, faBoxOpen,
    faKeyboard
} from "@fortawesome/free-solid-svg-icons";

// Componentes y Controladores
import InlineEntityFinder from '../../../components/entities/InlineEntityFinder';
import CreateEntityModal from '../../../components/entities/CreateEntityModal';
import UserContext from '../../../navigation/context/userContext';
import { createPosOrder, getPosProductDetails, initPos, searchPosProducts } from '../../../controllers/company/pos/PosCatalogController';

import ProductOptionsModal from './ProductOptionsModal';
import CustomItemModal from './CustomItemModal';
import PosPaymentModal from './PosPaymentModal';
import OrderSuccessModal from './OrderSuccessModal';
import { updateOrderStatus } from '../../../controllers/company/OrdersController';

export default function PosDashboard() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md')); 
    const [mobileCartOpen, setMobileCartOpen] = useState(false);

    const { userData } = useContext(UserContext);
    const [isLoading, setIsLoading] = useState(true);

    // --- ESTADOS PRINCIPALES ---
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [products, setProducts] = useState([]);
    const [cart, setCart] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [exchangeRate, setExchangeRate] = useState(1);
    const [countryId, setCountryId] = useState(null);
    const [showCustomItemModal, setShowCustomItemModal] = useState(false)

    const [paymentMethods, setPaymentMethods] = useState([]);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    
    // --- ESTADOS DEL CLIENTE ---
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [showCreateClient, setShowCreateClient] = useState(false);

    // --- ESTADOS DEL DETALLE DE PRODUCTO ---
    const [detailedProduct, setDetailedProduct] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loadingProductId, setLoadingProductId] = useState(null);

    const [appliedPayments, setAppliedPayments] = useState([]);
    const [completedOrderData, setCompletedOrderData] = useState(null);
    

    // --- CARGA INICIAL ---
    useEffect(() => {
        handleInitPos();
    }, []);

    const getActiveMerchantUuid = () => {
        return localStorage.getItem('work_mode_merchant_id') || userData?.entity?.uuid || null;
    };

    const handleInitPos = async () => {
        setIsLoading(true);
        const merchantUuid = getActiveMerchantUuid();
        
        if (merchantUuid) {
            const response = await initPos(merchantUuid);
            setCategories(response.categories || []);
            setProducts(response.top_products || []);
            setExchangeRate(response.exchange_rate || 1);
            setCountryId(response.country_id);
            setPaymentMethods(response.payment_methods || []);
        }
        setIsLoading(false);
    };

    const handleCategoryClick = async (categoryId) => {
        setIsLoading(true);
        setSelectedCategory(categoryId);
        const merchantUuid = getActiveMerchantUuid();
        
        if (categoryId === null) {
            await handleInitPos();
        } else {
            const filteredProducts = await searchPosProducts(merchantUuid, '', categoryId);
            setProducts(filteredProducts || []);
            setIsLoading(false);
        }
    };

    // --- ACCIONES DEL CARRITO ---
    // Reemplaza tus funciones actuales con estas:

    const handleProductClick = async (product) => {
        if (product.has_variants) {
            setLoadingProductId(product.id);
            const merchantUuid = getActiveMerchantUuid();
            
            const details = await getPosProductDetails(merchantUuid, product.id);
            
            // SIMULACIÓN: Asegúrate de usar tu función real de la API aquí
            //const details = await searchPosProducts(merchantUuid, product.id); // Reemplaza con getProductDetails real
            
            setDetailedProduct(details);
            setIsModalOpen(true);
            setLoadingProductId(null);
        } else {
            // Producto simple: el ID único en el carrito es su propio ID
            addToCartLogic(product, product.id, product.price, 1);
        }
    };

    const handleAddToCartFromModal = (product, options, modifiers, qty, finalPrice) => {
        let extrasNames = [];
        
        if (modifiers && typeof modifiers === 'object') {
            Object.keys(modifiers).forEach(groupId => {
                const selectedVal = modifiers[groupId];
                const selectedIds = Array.isArray(selectedVal) ? selectedVal : [selectedVal];

                // Buscamos el grupo en el producto original
                const group = product.modifier_groups?.find(g => String(g.id) === String(groupId));
                if (group) {
                    // Aquí cubrimos los nombres más comunes para la lista de opciones
                    const optionsList = group.modifiers || group.items || group.options || group.choices || [];
                    
                    selectedIds.forEach(id => {
                        const opt = optionsList.find(o => String(o.id) === String(id));
                        if (opt) {
                            extrasNames.push(opt.name); // Guardamos el texto real
                        }
                    });
                }
            });
        }

        const uniqueCartId = `${product.id}-${JSON.stringify(options)}-${JSON.stringify(modifiers)}`;
        
        // 👇 AHORA SÍ PASAMOS LOS MODIFIERS COMO ÚLTIMO PARÁMETRO
        addToCartLogic(product, uniqueCartId, finalPrice, qty, options, modifiers);
        setIsModalOpen(false);
    };

    const addToCartLogic = (product, cartItemId, price, quantityToAdd, options = null, modifiers = []) => {
        const existingItem = cart.find(item => item.cartItemId === cartItemId);
        if (existingItem) {
            setCart(cart.map(item => 
                item.cartItemId === cartItemId 
                ? { ...item, quantity: item.quantity + quantityToAdd } 
                : item
            ));
        } else {
            setCart([...cart, { 
                ...product, 
                cartItemId, 
                price, 
                quantity: quantityToAdd,
                selectedOptions: options,
                // 👇 GUARDAMOS LOS EXTRAS EN EL ESTADO DEL ITEM
                selectedModifiers: modifiers 
            }]);
        }
    };

    const updateQuantity = (cartItemId, value) => {
        if (value === '') {
            setCart(cart.map(item => item.cartItemId === cartItemId ? { ...item, quantity: '' } : item));
            return;
        }
        const parsedValue = parseFloat(value);
        if (isNaN(parsedValue) || parsedValue < 0) return;
        setCart(cart.map(item => item.cartItemId === cartItemId ? { ...item, quantity: parsedValue } : item));
    };

    const handleAddCustomItem = (customItem) => {
        // Usamos el ID ficticio generado como cartItemId
        const cartItemId = customItem.id; 
        
        setCart([...cart, { 
            ...customItem, 
            cartItemId: cartItemId // Mantenemos la convención de tu carrito
        }]);
    };

    const removeLine = (cartItemId) => setCart(cart.filter(item => item.cartItemId !== cartItemId));

    // --- CÁLCULOS ---
    const subtotal = cart.reduce((acc, item) => acc + (item.price * (parseFloat(item.quantity) || 0)), 0);
    const totalItems = cart.reduce((acc, item) => acc + (parseFloat(item.quantity) || 0), 0);
    const total = subtotal;

    // --- SKELETON LOADER PARA LA GRILLA ---
    const renderSkeletons = () => (
        <Grid container spacing={2}>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                <Grid item xs={6} sm={4} md={3} lg={2.4} key={n}>
                    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e2e8f0', height: 120 }}>
                        <Skeleton variant="text" width="50%" />
                        <Skeleton variant="text" width="80%" height={24} />
                        <Skeleton variant="rectangular" width="40%" height={20} sx={{ mt: 2, borderRadius: 1 }} />
                    </Paper>
                </Grid>
            ))}
        </Grid>
    );

    // --- UI DEL CARRITO ---
    const cartUI = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'white' }}>
            
            {/* Header Móvil */}
            {isMobile && (
                <Box sx={{ p: 2, bgcolor: '#0F172A', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight="600">Current Order</Typography>
                    <IconButton size="small" sx={{ color: 'white' }} onClick={() => setMobileCartOpen(false)}>
                        <FontAwesomeIcon icon={faTimes}/>
                    </IconButton>
                </Box>
            )}

            {/* Buscador de Clientes */}
            <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0' }}>
                <Typography variant="overline" color="text.secondary" fontWeight="700" sx={{ display: 'block', mb: 1, lineHeight: 1 }}>
                    CUSTOMER
                </Typography>
                {!selectedCustomer ? (
                    <InlineEntityFinder onSelectEntity={setSelectedCustomer} onAddNew={() => setShowCreateClient(true)} />
                ) : (
                    <Box sx={{ p: 1.5, bgcolor: '#f1f5f9', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography variant="subtitle2" fontWeight="700" color="#0F172A">{selectedCustomer.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{selectedCustomer.email || 'No email'}</Typography>
                        </Box>
                        <IconButton size="small" color="error" onClick={() => setSelectedCustomer(null)}>
                            <FontAwesomeIcon icon={faUserXmark} size="sm"/>
                        </IconButton>
                    </Box>
                )}
            </Box>

            {/* Lista de Productos en el Carrito */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2, bgcolor: '#f8fafc' }}>
                {cart.length === 0 ? (
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', opacity: 0.5 }}>
                        <FontAwesomeIcon icon={faBoxOpen} size="2x" color="#94a3b8" style={{ marginBottom: '8px' }} />
                        <Typography variant="body2" color="text.secondary" fontWeight="500">Cart is empty</Typography>
                    </Box>
                ) : (
                    <Stack spacing={1.5}>
                        {cart.map((item) => (
                            <Paper 
                                key={item.cartItemId || item.id} 
                                elevation={0} 
                                sx={{ 
                                    p: 1.5, 
                                    borderRadius: 2, 
                                    border: '1px solid #e2e8f0', 
                                    display: 'flex', 
                                    alignItems: 'flex-start', 
                                    justifyContent: 'space-between',
                                    bgcolor: 'white'
                                }}
                            >
                                {/* 1. Detalles del Producto (Izquierda) */}
                                <Box sx={{ flexGrow: 1, minWidth: 0, pr: 2, pt: 0.5 }}>
                                    <Typography variant="subtitle2" fontWeight="700" color="#0F172A" sx={{ lineHeight: 1.2 }}>
                                        {item.name}
                                    </Typography>
                                    {item.selectedOptions && (
                                        <Typography variant="caption" color="#64748b" display="block" sx={{ mt: 0.5 }}>
                                            {Object.values(item.selectedOptions).join(' • ')}
                                        </Typography>
                                    )}
                                    {/* 👇 NUEVO: Muestra los Extras (Plata, Mickey) */}
                                    {item.selectedModifiers && item.selectedModifiers.length > 0 && (
                                        <Typography variant="caption" color="#f59e0b" fontWeight="600" display="block">
                                            + {item.selectedModifiers.join(', ')}
                                        </Typography>
                                    )}
                                    <Typography variant="caption" color="#0ea5e9" fontWeight="700" sx={{ mt: 0.5, display: 'block' }}>
                                        ${item.price.toFixed(2)} <Typography component="span" variant="caption" color="text.secondary" fontWeight="500">c/u</Typography>
                                    </Typography>
                                </Box>
                                
                                {/* 2. Cálculos y Acciones (Derecha apilada) */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1.5 }}>
                                    
                                    {/* Fila Superior Derecha: Total del renglón y Botón de eliminar */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="subtitle2" fontWeight="900" color="#0F172A">
                                            ${((parseFloat(item.quantity) || 0) * item.price).toFixed(2)}
                                        </Typography>
                                        <IconButton 
                                            size="small" 
                                            onClick={() => removeLine(item.cartItemId || item.id)}
                                            sx={{ color: '#ef4444', p: 0.5, '&:hover': { bgcolor: '#fee2e2' } }}
                                        >
                                            <FontAwesomeIcon icon={faTrashAlt} size="xs"/>
                                        </IconButton>
                                    </Box>

                                    {/* Fila Inferior Derecha: Control de Cantidad (Soporta Decimales) */}
                                    <Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        border: '1px solid #e2e8f0', 
                                        borderRadius: '6px', 
                                        bgcolor: '#f8fafc',
                                        overflow: 'hidden'
                                    }}>
                                        <IconButton 
                                            size="small" 
                                            onClick={() => updateQuantity(item.cartItemId || item.id, Math.max(0, (parseFloat(item.quantity) || 0) - 1))} 
                                            sx={{ borderRadius: 0, p: '4px 8px', color: '#64748b' }}
                                        >
                                            <FontAwesomeIcon icon={faMinus} size="xs" />
                                        </IconButton>
                                        
                                        <TextField 
                                            value={item.quantity} 
                                            onChange={(e) => updateQuantity(item.cartItemId || item.id, e.target.value)} 
                                            variant="standard"
                                            inputProps={{ 
                                                // type="number" y step="any" son cruciales para permitir decimales fluidos
                                                type: 'number', 
                                                step: 'any', 
                                                min: '0',
                                                style: { 
                                                    textAlign: 'center', 
                                                    width: '50px', // Más ancho para acomodar números como "12.50"
                                                    fontSize: '0.9rem', 
                                                    fontWeight: '700', 
                                                    color: '#0F172A', 
                                                    padding: '2px 0' 
                                                } 
                                            }}
                                            InputProps={{ disableUnderline: true }}
                                            sx={{
                                                // Ocultar las flechas nativas del input number para mantener el diseño limpio
                                                '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
                                                    WebkitAppearance: 'none',
                                                    margin: 0
                                                },
                                                '& input[type=number]': {
                                                    MozAppearance: 'textfield'
                                                }
                                            }}
                                        />

                                        <IconButton 
                                            size="small" 
                                            onClick={() => updateQuantity(item.cartItemId || item.id, (parseFloat(item.quantity) || 0) + 1)} 
                                            sx={{ borderRadius: 0, p: '4px 8px', color: '#64748b' }}
                                        >
                                            <FontAwesomeIcon icon={faPlus} size="xs" />
                                        </IconButton>
                                    </Box>

                                </Box>
                            </Paper>
                        ))}
                    </Stack>
                )}
            </Box>

            {/* Panel de Cobro Inferior */}
            <Box sx={{ p: 2, bgcolor: '#0F172A', color: 'white', flexShrink: 0 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography variant="body2" color="#94a3b8">Subtotal</Typography>
                    <Typography variant="body2" fontWeight="600">${subtotal.toFixed(2)}</Typography>
                </Stack>
                
                {countryId === 1 && (
                    <Stack direction="row" justifyContent="space-between" mb={1}>
                        <Typography variant="caption" color="#94a3b8">Bs. Ref</Typography>
                        <Typography variant="caption" color="#4ade80" fontWeight="600">{(total * exchangeRate).toFixed(2)}</Typography>
                    </Stack>
                )}
                
                <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.1)' }} />
                
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" fontWeight="400">Total</Typography>
                    <Typography variant="h4" fontWeight="700" sx={{ color: 'white' }}>
                        ${total.toFixed(2)}
                    </Typography>
                </Stack>

                <Button 
                    fullWidth 
                    variant="contained" 
                    disabled={cart.length === 0 || !selectedCustomer} 
                    onClick={() => setShowPaymentModal(true)}
                    sx={{ 
                        py: 1.5, 
                        borderRadius: 1, 
                        fontSize: '1rem',
                        fontWeight: '600',
                        textTransform: 'none',
                        bgcolor: '#0ea5e9',
                        '&:hover': { bgcolor: '#0284c7' },
                        '&.Mui-disabled': { bgcolor: '#1e293b', color: '#475569' }
                    }}
                >
                    <FontAwesomeIcon icon={faMoneyBillWave} style={{ marginRight: '8px' }} />
                    Completar Orden
                </Button>
            </Box>
        </Box>
    );


    const handleCompleteOrder = async (paymentsToProcess, changeAmount) => {
        // 1. Mostrar estado de carga (opcional pero recomendado)
        setIsLoading(true); 
        
        const merchantUuid = getActiveMerchantUuid();

        //return console.log(cart);

        // 2. Estructurar el Payload exacto que espera el Backend
        const orderPayload = {
            customer_id: selectedCustomer.id,
            subtotal: subtotal,
            total: total,
            change_amount: changeAmount, // El vuelto (útil para auditorías de caja)

            status: 2, //finalizado
            
            // Mapeamos el carrito para enviar solo lo necesario
            items: cart.map(item => {
                // 1. Convertimos las Variantes (Talla, Color)
                let variantsArray = [];
                if (item.selectedOptions) {
                    variantsArray = Object.entries(item.selectedOptions).map(
                        ([key, value]) => `${key}: ${value}`
                    );
                }

                // 2. BUSCADOR PROFUNDO PARA LOS EXTRAS
                // 2. BUSCADOR PROFUNDO PARA LOS EXTRAS
                let extrasArray = [];
                if (item.selectedModifiers && typeof item.selectedModifiers === 'object') {
                    
                    let idsToFind = [];
                    Object.values(item.selectedModifiers).forEach(val => {
                        if (Array.isArray(val)) {
                            idsToFind.push(...val);
                        } else {
                            idsToFind.push(val);
                        }
                    });

                    if (item.modifier_groups && Array.isArray(item.modifier_groups)) {
                        item.modifier_groups.forEach(group => {
                            const optionsList = group.modifiers || group.items || group.options || group.choices || group.variants || [];
                            
                            // 👇 EXTRAEMOS EL NOMBRE DEL GRUPO (ej: "Broches", "Dibujos")
                            // Usamos fallback a 'Extra' por si el grupo no tiene nombre definido
                            const groupName = group.name || group.title || 'Extra'; 
                            
                            optionsList.forEach(opt => {
                                if (idsToFind.some(id => String(id) === String(opt.id))) {
                                    
                                    // 👇 ARMAMOS EL STRING DESCRIPTIVO
                                    extrasArray.push(`${groupName}: ${opt.name}`); 
                                    
                                }
                            });
                        });
                    }
                }

                // 3. Retornamos la estructura exacta que pide el backend de Laravel
                return {
                    product_id: item.id,
                    quantity: item.quantity,
                    price: item.price,
                    name: item.name,
                    options: {
                        variants: variantsArray,
                        extras: extrasArray 
                    }
                };
            }),

            // Mapeamos los pagos procesados en el modal
            payments: paymentsToProcess.map(payment => ({
                payment_method_id: payment.method_id,
                account_number_id: payment.account_id, // Puede ser null si es Efectivo
                amount: payment.amount,
                reference: payment.reference || null
            }))
        };

        // 3. Ejecutar la petición al backend
        const result = await createPosOrder(merchantUuid, orderPayload);

        setIsLoading(false);

        // 4. Manejar la respuesta
        if (result) {
            
            setShowPaymentModal(false);            
            setCompletedOrderData(result);

            // Aquí podríamos agregar la lógica para imprimir el ticket
            // printReceipt(result.receipt_url);
            
        } else {
            // ERROR
            alert("Hubo un error al procesar la orden. Por favor intente nuevamente.");
        }
    };

    const resetPosForNewClient = () => {
        setCompletedOrderData(null);
        setCart([]);
        setSelectedCustomer(null);
        setAppliedPayments([]);
    };

    const handleUpdateOrderStatus = async (orderId, newStatus) => {
        setIsLoading(true);
        // Aquí llamas a tu API real para cambiar el estatus de la orden
        // await api.put(`/api/orders/${orderId}/status`, { status: newStatus });
        await updateOrderStatus(orderId,newStatus);
        //console.log(`Orden ${orderId} actualizada al estatus ${newStatus}`);
        
        setIsLoading(false);
        resetPosForNewClient(); // Limpiamos la pantalla
    };


    return (
        // Uso de Flexbox estricto en lugar de Grid para evitar gutters y espacios flotantes
        <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden', bgcolor: '#f8fafc' }}>
            
            <CreateEntityModal open={showCreateClient} onClose={() => setShowCreateClient(false)} onSuccess={(e) => { setSelectedCustomer(e); setShowCreateClient(false); }} />
            
            {/* --- COLUMNA IZQUIERDA: CATÁLOGO --- */}
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', p: isMobile ? 2 : 3 }}>
                
                {/* --- Barra superior de búsqueda y acciones rápidas --- */}
                <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                    
                    {/* Buscador */}
                    <Paper elevation={0} sx={{ flexGrow: 1, p: '2px 4px', display: 'flex', alignItems: 'center', border: '1px solid #e2e8f0', borderRadius: 2 }}>
                        <IconButton sx={{ p: '10px' }} disabled>
                            <FontAwesomeIcon icon={faSearch} size="sm" color="#94a3b8" />
                        </IconButton>
                        <TextField 
                            fullWidth 
                            placeholder="Search products..." 
                            variant="standard"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{ disableUnderline: true, sx: { fontSize: '0.95rem' } }} 
                        />
                    </Paper>

                    {/* Botón de Producto Libre */}
                    <Button 
                        variant="outlined" 
                        onClick={() => setShowCustomItemModal(true)}
                        sx={{ 
                            borderRadius: 2, 
                            borderColor: '#e2e8f0', 
                            color: '#0F172A',
                            fontWeight: '700',
                            px: 2,
                            textTransform: 'none',
                            bgcolor: 'white',
                            '&:hover': { bgcolor: '#f1f5f9', borderColor: '#cbd5e1' }
                        }}
                    >
                        <FontAwesomeIcon icon={faKeyboard} style={{ marginRight: '8px', color: '#64748b' }} />
                        Manual
                    </Button>
                    <CustomItemModal 
                        open={showCustomItemModal} 
                        onClose={() => setShowCustomItemModal(false)} 
                        onAdd={handleAddCustomItem} 
                    />
                </Box>


                {/* Filtros / Categorías */}
                <Box 
                    sx={{ 
                        mb: 2, 
                        width: '100%',
                        height:60,
                        overflowX: 'auto', 
                        
                        // Estilos de la barra de scroll para PC
                        '&::-webkit-scrollbar': { height: '6px' },
                        '&::-webkit-scrollbar-track': { background: 'transparent' },
                        '&::-webkit-scrollbar-thumb': { background: '#cbd5e1', borderRadius: '10px' },
                        '&::-webkit-scrollbar-thumb:hover': { background: '#94a3b8' }
                    }}
                >
                    {/* 👇 LA MAGIA AQUÍ: p: 0.5 separa los Chips 4px de los bordes del contenedor para evitar cortes */}
                    <Stack direction="row" spacing={1} sx={{ p: 0.5, alignItems: 'center' }}>
                        
                        <Chip 
                            label="Top Sales"
                            onClick={() => handleCategoryClick(null)}
                            sx={{ 
                                flexShrink: 0,
                                borderRadius: 1.5, 
                                fontWeight: '600', 
                                cursor: 'pointer',
                                bgcolor: selectedCategory === null ? '#0F172A' : 'transparent',
                                color: selectedCategory === null ? 'white' : '#475569',
                                border: selectedCategory === null ? '1px solid #0F172A' : '1px solid #cbd5e1',
                                '&:hover': { bgcolor: selectedCategory === null ? '#1e293b' : '#f1f5f9' }
                            }}
                        />
                        
                        {categories.map(c => (
                            <Chip 
                                key={c.id} 
                                label={c.name}
                                onClick={() => handleCategoryClick(c.id)}
                                sx={{ 
                                    flexShrink: 0,
                                    borderRadius: 1.5, 
                                    fontWeight: '600', 
                                    cursor: 'pointer',
                                    bgcolor: selectedCategory === c.id ? '#0F172A' : 'transparent',
                                    color: selectedCategory === c.id ? 'white' : '#475569',
                                    border: selectedCategory === c.id ? '1px solid #0F172A' : '1px solid #cbd5e1',
                                    '&:hover': { bgcolor: selectedCategory === c.id ? '#1e293b' : '#f1f5f9' }
                                }}
                            />
                        ))}
                        
                    </Stack>
                </Box>

                {/* Grilla de Productos */}
                <Box sx={{ flexGrow: 1, overflowY: 'auto', pb: 10, pr: 1 }}>
                    {isLoading ? (
                        renderSkeletons()
                    ) : (
                        <Grid container spacing={2}>
                            {products.map(p => (
                                <Grid item xs={6} sm={4} md={3} lg={2.4} key={p.id}>
                                    <CardActionArea 
                                        onClick={() => handleProductClick(p)}
                                        sx={{ height: '100%', borderRadius: 2 }}
                                    >
                                        <Paper elevation={0} sx={{ 
                                            p: 2, 
                                            height: '120px',
                                            display: 'flex', 
                                            flexDirection: 'column', 
                                            justifyContent: 'space-between',
                                            borderRadius: 2, 
                                            border: '1px solid #e2e8f0',
                                            bgcolor: 'white',
                                            transition: 'border-color 0.2s',
                                            '&:hover': { borderColor: '#94a3b8' }
                                        }}>
                                            <Box>
                                                <Typography variant="caption" fontWeight="700" color="text.secondary" sx={{ display: 'block', mb: 0.5, lineHeight: 1 }}>
                                                    {p.category}
                                                </Typography>
                                                <Typography variant="subtitle2" fontWeight="600" color="#0F172A" sx={{ lineHeight: 1.2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                    {p.name}
                                                </Typography>
                                            </Box>
                                            <Typography variant="subtitle1" color="#0F172A" fontWeight="700">
                                                ${p.price.toFixed(2)}
                                            </Typography>
                                        </Paper>
                                    </CardActionArea>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Box>
            </Box>

            {/* --- COLUMNA DERECHA: CARRITO (DESKTOP) --- */}
            {!isMobile && (
                <Box sx={{ width: '380px', flexShrink: 0, borderLeft: '1px solid #e2e8f0', bgcolor: 'white' }}>
                    {cartUI}
                </Box>
            )}

            {/* --- ELEMENTOS MÓVILES --- */}
            {isMobile && (
                <>
                    <Fab 
                        variant="extended"
                        onClick={() => setMobileCartOpen(true)}
                        sx={{ position: 'fixed', bottom: 20, left: 20, right: 20, bgcolor: '#0F172A', color: 'white', justifyContent: 'space-between', px: 3 }}
                    >
                        <Badge badgeContent={totalItems} color="error">
                            <FontAwesomeIcon icon={faShoppingCart} />
                        </Badge>
                        <Typography fontWeight="600">View Order</Typography>
                        <Typography fontWeight="700">${total.toFixed(2)}</Typography>
                    </Fab>
                    <Drawer anchor="right" open={mobileCartOpen} onClose={() => setMobileCartOpen(false)} PaperProps={{ sx: { width: '100%', sm: '400px' } }}>
                        {cartUI}
                    </Drawer>
                </>
            )}
            
            <ProductOptionsModal 
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                product={detailedProduct}
                onAddToCart={handleAddToCartFromModal}
            />

            <PosPaymentModal 
                open={showPaymentModal}
                onClose={() => setShowPaymentModal(false)}
                total={total}
                paymentMethods={paymentMethods}
                onCompleteOrder={handleCompleteOrder}
                
                // Pasamos el estado de los pagos y las variables de Venezuela
                appliedPayments={appliedPayments}
                setAppliedPayments={setAppliedPayments}
                countryId={countryId}
                exchangeRate={exchangeRate}
            />

            <OrderSuccessModal 
                open={Boolean(completedOrderData)}
                order={completedOrderData}
                onClose={resetPosForNewClient}
                onUpdateStatus={handleUpdateOrderStatus}
            />
        </Box>
    );
}